'use client';

/* ─────────────────────────────────────────────────────────────────────────────
   Shared shimmer animation — injected once, reused by all skeletons
───────────────────────────────────────────────────────────────────────────── */
const SHIMMER_CSS = `
@keyframes ennix-sk-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
}
.ennix-sk {
    background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
    background-size: 200% 100%;
    animation: ennix-sk-shimmer 1.5s ease-in-out infinite;
    border-radius: 6px;
}
`;

const ShimmerStyle = () => <style>{SHIMMER_CSS}</style>;

const SK = ({ w = '100%', h = 12, r = 6, style = {} }) => (
    <div
        className="ennix-sk"
        style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }}
    />
);

/* ── Offer bar skeleton ───────────────────────────────────────────────────── */
export const OfferBarSkeleton = () => (
    <>
        <ShimmerStyle />
        <div style={{
            height: 36, background: '#e8eaed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            padding: '0 16px',
        }}>
            <SK w={60} h={14} r={99} />
            <SK w={220} h={12} r={6} />
            <SK w={40} h={14} r={99} />
        </div>
    </>
);

/* ── Category pills skeleton ─────────────────────────────────────────────── */
export const CategoryListSkeleton = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, padding: '4px 0' }}>
        <ShimmerStyle />
        {/* Pills row */}
        <div style={{ display: 'flex', gap: 8, overflow: 'hidden', flex: 1 }}>
            {[88, 112, 96, 130, 100].map((w, i) => (
                <SK key={i} w={w} h={34} r={99} style={{ flexShrink: 0 }} />
            ))}
        </div>
        {/* Shipping estimate button placeholder */}
        <SK w={180} h={42} r={12} style={{ flexShrink: 0 }} />
    </div>
);

/* ── Delivery check card skeleton ────────────────────────────────────────── */
export const DeliveryCheckSkeleton = () => (
    <div style={{
        border: '1px solid rgba(11,49,99,0.12)', borderRadius: 10,
        overflow: 'hidden', marginTop: 12, marginBottom: 8,
    }}>
        <ShimmerStyle />
        {/* Header bar */}
        <div style={{
            height: 40, background: '#e2e8f0',
            display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px',
        }}>
            <SK w={16} h={16} r={50} />
            <SK w={80} h={14} r={6} />
        </div>
        {/* Input row */}
        <div style={{ padding: '12px 16px', display: 'flex', gap: 8 }}>
            <SK w="100%" h={40} r={8} />
            <SK w={72} h={40} r={8} style={{ flexShrink: 0 }} />
        </div>
    </div>
);

/* ── Single product card skeleton ────────────────────────────────────────── */
export const ProductCardSkeleton = () => (
    <div style={{
        borderRadius: 14, overflow: 'hidden',
        background: '#fff',
        border: '1px solid #f0f2f5',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
        <ShimmerStyle />

        {/* Image area */}
        <div style={{ padding: 12, paddingBottom: 4 }}>
            <SK w="100%" h={190} r={10} />
        </div>

        {/* Body */}
        <div style={{ padding: '10px 12px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* Title */}
            <SK w="90%" h={12} />
            <SK w="65%" h={12} />

            {/* Price row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <SK w={80} h={26} r={6} />
                <SK w={50} h={18} r={99} />
            </div>

            {/* MRP row */}
            <SK w="55%" h={10} />

            {/* 3 mini action buttons */}
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <SK w="33%" h={38} r={8} />
                <SK w="33%" h={38} r={8} />
                <SK w="33%" h={38} r={8} />
            </div>

            {/* Buy / Cart row */}
            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                <SK w="50%" h={36} r={8} />
                <SK w="50%" h={36} r={8} />
            </div>
        </div>
    </div>
);

/* ── Product list grid skeleton ──────────────────────────────────────────── */
export const ProductListSkeleton = ({ count = 8 }) => (
    <div className="grid product-list" style={{ marginTop: 8 }}>
        <ShimmerStyle />
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="col md:col-4 xl:col-3 p-3">
                <ProductCardSkeleton />
            </div>
        ))}
    </div>
);

/* ── Full home page skeleton ─────────────────────────────────────────────── */
export const HomePageSkeleton = () => (
    <div className="surface-10 border-1 border-100 border-round-md">
        <ShimmerStyle />

        {/* Sticky offer bar */}
        <div style={{ position: 'sticky', top: 0, zIndex: 200 }}>
            <OfferBarSkeleton />
        </div>

        {/* TagLine placeholder */}
        <div style={{ padding: '16px 0 8px', display: 'flex', justifyContent: 'center' }}>
            <SK w={260} h={18} r={6} />
        </div>

        <div className="container pb-4 pt-3">
            {/* Category pills */}
            <CategoryListSkeleton />

            {/* Product grid */}
            <ProductListSkeleton count={8} />
        </div>
    </div>
);
